import {
  PageBlueprint,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import { AppIcon } from '@backstage/core-components';

/**
 * Custom replacement for the stock `page:api-docs` extension shipped
 * by `@backstage/plugin-api-docs/alpha`. We override it for exactly
 * the same reason we override `page:catalog` (see
 * `packages/app/src/modules/catalog/index.tsx`): the upstream loader
 * (`NfsApiExplorerPage`) renders a `<HeaderPage>` from
 * `@backstage/ui` showing "Bosch Digital Factory API Explorer" + a
 * Register button, which stacks underneath our blue
 * `BoschPageLayout` banner.
 *
 * The replacement page (`ApiDocsIndexPage` in
 * `packages/app/src/components/apiDocs/ApiDocsIndexPage.tsx`)
 * starts straight at `<Content>` and right-aligns the Register /
 * Support buttons in a `<ContentHeader>` row.
 *
 * Both the upstream page AND the upstream nav item are disabled in
 * `app-config.yaml`:
 *
 *     extensions:
 *       - page:api-docs: false
 *       - nav-item:api-docs: false
 *
 * Without the page disable, two extensions would attach to
 * `/api-docs`. Without the nav-item disable, the sidebar would show
 * two "APIs" entries.
 *
 * The sidebar config in `packages/app/src/modules/nav/Sidebar.tsx`
 * picks our nav item via the catch-all `nav.rest({ sortBy: 'title' })`
 * (api-docs is NOT one of the explicitly-pinned items like
 * page:home / page:catalog / page:scaffolder), so no Sidebar.tsx
 * change is needed — the framework auto-derives a nav entry from
 * the `title` + `icon` we pass to `PageBlueprint.make` below.
 *
 * Note we deliberately do NOT pass `noHeader: true`. We WANT our
 * `BoschPageLayout` banner ("API Docs" + subtitle from the
 * `PAGE_OVERRIDES` map in `pageLayout.tsx`) to render. The
 * `ApiDocsIndexPage` loader intentionally does NOT call `<Header>`
 * itself.
 *
 * ⚠️ ROUTE REF — REUSE THE UPSTREAM ONE.
 *
 * We pass `apiDocsPlugin.routes.root` here instead of a fresh
 * `createRouteRef()`. Same reason as `catalogModule`: the upstream
 * api-docs plugin (and other plugins like the entity layout) call
 * `useRouteRef(apiDocsPlugin.routes.root)` to navigate to the API
 * explorer. Disabling `page:api-docs` removes the binding for that
 * routeRef, and a fresh routeRef would leave dependants throwing
 * `No path for routeRef{id=api-docs.root}` at runtime. See the
 * matching block in `packages/app/src/modules/catalog/index.tsx`.
 */
const apiDocsIndexPage = PageBlueprint.make({
  params: {
    path: '/api-docs',
    title: 'APIs',
    icon: <AppIcon id="kind:api" />,
    routeRef: apiDocsPlugin.routes.root,
    loader: () =>
      import('../../components/apiDocs/ApiDocsIndexPage').then(m => (
        <m.ApiDocsIndexPage />
      )),
  },
});

/**
 * Registered as a frontend PLUGIN (not a module) so the framework
 * auto-creates a sidebar nav item from the `PageBlueprint`'s
 * `title` + `icon`. Same pattern as `techdocsModule` /
 * `permissionsModule` / `kindsModule`. `pluginId: 'api-docs-override'`
 * avoids colliding with the upstream `api-docs` pluginId.
 *
 * No `routes` field — we don't expose any new route refs, we just
 * re-bind the upstream `apiDocsPlugin.routes.root` via the
 * PageBlueprint above.
 */
export const apiDocsModule = createFrontendPlugin({
  pluginId: 'api-docs-override',
  extensions: [apiDocsIndexPage],
});
